import {
  CLEF_INFO,
  landmarkPosition,
  middleCPosition,
  pitchAtPosition,
  staffLineNames,
  staffPosition,
  staffSpaceNames,
  type Clef,
} from '../clef.ts';
import { diatonicIndex, parsePitchId, pitchId, pitchLabel, type Accidental, type SpelledPitch } from '../pitch.ts';
import { validateNoteIdConfig, type NoteIdConfig } from '../exercises/note-identification/config.ts';
import { generateRhythmQuestions } from '../rhythm/exercise.ts';
import { LESSONS, UNITS, MILESTONES } from './note-reading.ts';
import { RHYTHM_LESSONS, RHYTHM_MILESTONES, RHYTHM_UNITS } from './rhythm.ts';
import type { Curriculum, ExplanationCard, LessonDefinition, PositionRef, RhythmExample, RhythmLessonExerciseSpec, UnitDefinition } from './types.ts';

export const CURRICULUM_ID = 'note-reading';

/** Every lesson and unit of both paths (content migrations, lookups). */
export const ALL_LESSONS: readonly LessonDefinition[] = [...LESSONS, ...RHYTHM_LESSONS];
export const ALL_UNITS: readonly UnitDefinition[] = [...UNITS, ...RHYTHM_UNITS];

export const LESSON_BY_ID: ReadonlyMap<string, LessonDefinition> = new Map(ALL_LESSONS.map((l) => [l.id, l]));
export const UNIT_BY_ID: ReadonlyMap<string, UnitDefinition> = new Map(ALL_UNITS.map((u) => [u.id, u]));
export const MILESTONE_BY_ID = new Map([...MILESTONES, ...RHYTHM_MILESTONES].map((m) => [m.id, m]));

export function unitCurriculum(unit: UnitDefinition): Curriculum {
  return unit.curriculum ?? 'note-reading';
}

export function lessonCurriculum(lesson: LessonDefinition): Curriculum {
  const unit = UNIT_BY_ID.get(lesson.unitId);
  return unit ? unitCurriculum(unit) : lesson.exercise.type === 'rhythm' ? 'rhythm' : 'note-reading';
}

export function unitsOf(curriculum: Curriculum): readonly UnitDefinition[] {
  return curriculum === 'rhythm' ? RHYTHM_UNITS : UNITS;
}

/** Lessons in path order (unit order, then lesson order within the unit). */
export function pathOrder(curriculum: Curriculum): readonly string[] {
  return unitsOf(curriculum).flatMap((u) => u.lessonIds);
}

/** The note-reading path order. */
export const PATH_ORDER: readonly string[] = pathOrder('note-reading');

/** Questions in a lesson's practice (all parts of a rhythm lesson). */
export function lessonQuestionCount(lesson: Pick<LessonDefinition, 'exercise'>): number {
  return lesson.exercise.type === 'rhythm' ? lesson.exercise.parts.reduce((n, p) => n + p.questionCount, 0) : lesson.exercise.questionCount;
}

/** A rhythm lesson's questions: each part generated from its own config (and seed), in order. */
export function generateRhythmLessonQuestions(spec: RhythmLessonExerciseSpec, seed: string) {
  let position = 0;
  return spec.parts.flatMap((part, i) => generateRhythmQuestions(part, `${seed}:${i}`).map((q) => ({ ...q, position: position++ })));
}

export function getLesson(id: string): LessonDefinition {
  const lesson = LESSON_BY_ID.get(id);
  if (!lesson) throw new Error(`Unknown lesson ${id}`);
  return lesson;
}

export function resolvePosition(ref: PositionRef, clef: Clef): number {
  if (typeof ref === 'number') return ref;
  const match = /^(landmark|middleC)(?:([+-])(\d+))?$/.exec(ref);
  if (!match) throw new Error(`Invalid position ref ${ref}`);
  const base = match[1] === 'landmark' ? landmarkPosition(clef) : middleCPosition(clef);
  const offset = match[3] ? Number(match[3]) * (match[2] === '-' ? -1 : 1) : 0;
  return base + offset;
}

export interface TrackContext {
  clef: Clef;
  /** Instrument written range; lesson ranges are clipped to it when enough notes remain. */
  rangeLow?: string | null;
  rangeHigh?: string | null;
}

/** Minimum distinct staff positions a clipped lesson must keep; otherwise it is not clipped. */
export const MIN_CLIPPED_POSITIONS = 3;

export function lessonPositions(lesson: LessonDefinition, context: TrackContext): number[] {
  const exercise = lesson.exercise;
  if (exercise.type !== 'note-identification') return [];
  const spec = exercise.positions;
  const all = Array.isArray(spec)
    ? spec.map((ref) => resolvePosition(ref, context.clef))
    : Array.from({ length: spec.to - spec.from + 1 }, (_, i) => spec.from + i);
  const filtered = all.filter((pos) => {
    if (exercise.staffFilter === 'lines') return pos % 2 === 0;
    if (exercise.staffFilter === 'spaces') return Math.abs(pos % 2) === 1;
    return true;
  });
  const low = context.rangeLow ? parsePitchId(context.rangeLow) : null;
  const high = context.rangeHigh ? parsePitchId(context.rangeHigh) : null;
  if (!low || !high) return filtered;
  const lowPos = staffPosition(context.clef, low);
  const highPos = staffPosition(context.clef, high);
  const clipped = filtered.filter((pos) => pos >= lowPos && pos <= highPos);
  return clipped.length >= MIN_CLIPPED_POSITIONS ? clipped : filtered;
}

/** The note-identification configuration for a lesson on a learner's track. */
export function resolveLessonConfig(lesson: LessonDefinition, context: TrackContext): NoteIdConfig {
  if (lesson.exercise.type !== 'note-identification') throw new Error(`Lesson ${lesson.id} is not a note-reading lesson`);
  const positions = lessonPositions(lesson, context);
  const pitches = positions.map((pos) => pitchAtPosition(context.clef, pos));
  const sorted = [...pitches].sort((a, b) => diatonicIndex(a) - diatonicIndex(b));
  const lowest = sorted[0] as SpelledPitch;
  const highest = sorted[sorted.length - 1] as SpelledPitch;
  const accidentals = lesson.exercise.accidentals;
  const practical = CLEF_INFO[context.clef].practicalRange;
  // Extend range endpoints so accidentals on the outer notes are included, without leaving the clef's supported range.
  const low: SpelledPitch =
    accidentals.includes('flat') && diatonicIndex(lowest) > diatonicIndex(practical.low) ? { ...lowest, accidental: 'flat' } : lowest;
  const high: SpelledPitch =
    accidentals.includes('sharp') && diatonicIndex(highest) < diatonicIndex(practical.high) ? { ...highest, accidental: 'sharp' } : highest;

  const candidate = {
    clef: context.clef,
    range: { low: pitchId(low), high: pitchId(high) },
    accidentals,
    answerMode: lesson.exercise.answerMode,
    questionCount: lesson.exercise.questionCount,
    staffFilter: lesson.exercise.staffFilter,
    pool: pitches.map(pitchId),
    accidentalRate: lesson.exercise.accidentalRate ?? 0.5,
    includeRareSpellings: false,
    hintsAllowed: true,
  };
  const result = validateNoteIdConfig(candidate);
  if (!result.ok) {
    throw new Error(`Lesson ${lesson.id} resolves to an invalid config in ${context.clef}: ${result.issues.map((i) => i.message).join('; ')}`);
  }
  return result.value;
}

function tokenValues(clef: Clef): Record<string, string> {
  const info = CLEF_INFO[clef];
  const landmark = info.landmark.pitch;
  const landmarkPos = landmarkPosition(clef);
  const middleC = middleCPosition(clef);
  const middleCWhere =
    middleC < 0
      ? `In ${clef} clef, middle C is below the staff (${Math.floor(-middleC / 2)} ledger line${Math.floor(-middleC / 2) === 1 ? '' : 's'} down).`
      : middleC > 8
        ? `In ${clef} clef, middle C is above the staff (${Math.floor((middleC - 8) / 2)} ledger line${Math.floor((middleC - 8) / 2) === 1 ? '' : 's'} up).`
        : `In ${clef} clef, middle C sits on the staff itself.`;
  return {
    clef,
    landmark: pitchLabel(landmark),
    landmarkLetter: landmark.letter,
    landmarkUp: pitchLabel(pitchAtPosition(clef, landmarkPos + 1)),
    landmarkDown: pitchLabel(pitchAtPosition(clef, landmarkPos - 1)),
    landmarkSentence: info.landmark.description,
    lines: staffLineNames(clef).map((x) => x.letter).join(' '),
    spaces: staffSpaceNames(clef).map((x) => x.letter).join(' '),
    bottomLine: pitchLabel(pitchAtPosition(clef, 0)),
    topLine: pitchLabel(pitchAtPosition(clef, 8)),
    middleCSentence: middleCWhere,
  };
}

export function renderLessonText(text: string, clef: Clef): string {
  const values = tokenValues(clef);
  return text.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole);
}

export interface ResolvedCard {
  title: string;
  body: string;
  example?: { clef: Clef; pitches: string[]; caption: string };
  rhythmExample?: RhythmExample;
}

export function resolveCards(cards: readonly ExplanationCard[], clef: Clef): ResolvedCard[] {
  return cards.map((card) => {
    const resolved: ResolvedCard = { title: renderLessonText(card.title, clef), body: renderLessonText(card.body, clef) };
    if (card.example) {
      const accidentals: Accidental[] = card.example.accidentals ?? ['natural'];
      const pitches = card.example.positions.flatMap((ref) => {
        const natural = pitchAtPosition(clef, resolvePosition(ref, clef));
        return accidentals.map((accidental) => pitchId({ ...natural, accidental }));
      });
      resolved.example = { clef, pitches, caption: renderLessonText(card.example.caption, clef) };
    }
    if (card.rhythmExample) resolved.rhythmExample = card.rhythmExample;
    return resolved;
  });
}

// ---------------------------------------------------------------------------
// Path progression
// ---------------------------------------------------------------------------

export type LessonStatus = 'completed' | 'placed' | 'available' | 'locked';

export interface LessonProgressRecord {
  lessonId: string;
  status: 'completed' | 'placed';
}

/**
 * Explicit path rules:
 *  - A lesson is available when every prerequisite is completed or placed-out.
 *  - Placed lessons count as satisfied prerequisites but stay open for review.
 *  - The "next lesson" is the first available, not-yet-satisfied lesson in path
 *    order that is not optional. Optional lessons are available but never next.
 *  - Assignment practice never marks path lessons complete by itself.
 */
export function lessonStatuses(progress: readonly LessonProgressRecord[], curriculum: Curriculum = 'note-reading'): Map<string, LessonStatus> {
  const satisfied = new Map(progress.map((p) => [p.lessonId, p.status] as const));
  const out = new Map<string, LessonStatus>();
  for (const id of pathOrder(curriculum)) {
    const own = satisfied.get(id);
    if (own) {
      out.set(id, own);
      continue;
    }
    const lesson = getLesson(id);
    const unlocked = lesson.prerequisites.every((pre) => satisfied.has(pre));
    out.set(id, unlocked ? 'available' : 'locked');
  }
  return out;
}

/**
 * The next lesson: the first available, unsatisfied, required lesson in path
 * order. A unit test that is open early (to skip ahead) is never "next" while
 * its unit's lessons are still ahead of it.
 */
export function nextLessonId(progress: readonly LessonProgressRecord[], curriculum: Curriculum = 'note-reading'): string | null {
  const statuses = lessonStatuses(progress, curriculum);
  for (const id of pathOrder(curriculum)) {
    const lesson = getLesson(id);
    if (lesson.optional) continue;
    if (statuses.get(id) === 'available') return id;
  }
  return null;
}

export function isLessonAvailable(lessonId: string, progress: readonly LessonProgressRecord[]): boolean {
  const lesson = LESSON_BY_ID.get(lessonId);
  const status = lessonStatuses(progress, lesson ? lessonCurriculum(lesson) : 'note-reading').get(lessonId);
  return status !== undefined && status !== 'locked';
}

/** Lessons strictly before `lessonId` in required path order (used by placement). */
export function requiredLessonsBefore(lessonId: string): string[] {
  const order = pathOrder(lessonCurriculum(getLesson(lessonId)));
  const index = order.indexOf(lessonId);
  if (index < 0) throw new Error(`Unknown lesson ${lessonId}`);
  return order.slice(0, index).filter((id) => !getLesson(id).optional);
}
